/**
 * Beetegre V2 — para birimi sabitleri (ISO 4217).
 *
 * Backend `CurrencyCodes.All` ile birebir aynı kümeyi taşır. M3 (AB#922) "Operations
 * Defaults" sayfası açıldığında bu liste runtime'da tenant ayarından türetilebilir
 * (settings API'sinden gelir); o zamana kadar sabit liste yeterli.
 *
 * Magic-string yasağı (CLAUDE.md "Constants and Magic Strings"): hiçbir bileşen
 * `'TRY'` / `'EUR'` literal kullanmaz — bu dosyadan import eder.
 */

export const DEFAULT_CURRENCY = 'TRY' as const;

export const SUPPORTED_CURRENCIES = [
  'TRY',
  'EUR',
  'USD',
  'GBP',
  'AED',
  'CHF',
  'JPY',
  'CNY',
  'RUB',
  'SAR',
] as const;

export type SupportedCurrency = (typeof SUPPORTED_CURRENCIES)[number];

/** Ready-made `OptionSelect` options for every supported currency. */
export const CURRENCY_OPTIONS = SUPPORTED_CURRENCIES.map((c) => ({ value: c as string, label: c as string }));

/**
 * Rapor filtresi için sunulan para birimleri (TRY + başlıca yabancı para birimleri).
 * SUPPORTED_CURRENCIES'in alt kümesidir; rapor sayfaları bu sabitten import eder.
 */
export const REPORT_CURRENCIES = ['TRY', 'USD', 'EUR'] as const;

/**
 * Fatura oluştururken seçilebilen para birimleri — şimdilik yalnızca TRY.
 *
 * Backend TRY dışı fatura için `ExchangeRate` zorunlu tutuyor
 * (`CreateInvoiceRequestValidator`), ama `CreateInvoiceRequest` tipinde o alan hiç yok:
 * yani TRY dışı bir fatura gönderilemez, denendiğinde 400 döner. Dokuz çalışmayan
 * seçenek sunmaktansa liste daraltıldı. Kur alanı eklendiğinde bu sabit kaldırılıp
 * yerine SUPPORTED_CURRENCIES kullanılacak (AB#1741).
 */
export const INVOICE_CURRENCIES = ['TRY'] as const;

/**
 * Fatura formunun para birimi seçeneklerini verir: izinli küme, artı kaydın hâlihazırda
 * taşıdığı değer eğer o kümede değilse.
 *
 * İkinci kısım kısıtlamanın dışladığı case: kısıtlamadan önce kaydedilmiş TRY dışı bir
 * fatura açıldığında değeri listede bulunmazsa Select kendi value'sunu çözemez ve alan
 * boş görünür. Alan düzenleme modunda zaten salt-okunur; buradaki amaç mevcut değeri
 * görünür tutmak, yeni seçime izin vermek değil.
 */
export function invoiceCurrencyOptions(current: string | null | undefined): string[] {
  const allowed: string[] = [...INVOICE_CURRENCIES];
  return current && !allowed.includes(current) ? [...allowed, current] : allowed;
}

/**
 * Bu para biriminde fatura kesilebilir mi? (case-insensitive)
 *
 * Seçicinin dışındaki yollar da bunu sormak zorunda: bir fiyat listesi çözümü form para
 * birimini doğrudan değiştirebiliyor ve seçici daraltılmış olsa bile formu izinli kümenin
 * dışına taşıyabiliyordu (AB#1744).
 */
export function isInvoiceCurrency(code: string | null | undefined): boolean {
  if (!code) return false;
  return (INVOICE_CURRENCIES as readonly string[]).includes(code.toUpperCase());
}

/** ISO 4217 kümesinde mi? (case-insensitive bound check) */
export function isKnownCurrency(code: string | null | undefined): boolean {
  if (!code || typeof code !== 'string') return false;
  const upper = code.toUpperCase();
  return SUPPORTED_CURRENCIES.includes(upper as SupportedCurrency);
}
